import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/**
 * Everything between "the browser will let us" and "the server has an address
 * to send to".
 *
 * The shape of this file is dictated by one thing: **permission is granted per
 * browser, per device, and cannot be asked for from anywhere but a real user
 * gesture.** So none of this is a setting that travels with the account -- it
 * is a thing you do on each device you want to be buzzed on, which is exactly
 * how the UI has to present it too (see usePushNotifications).
 */

/** Same path the service worker is served from -- it is a public/ file, deliberately not bundled. */
const SERVICE_WORKER_URL = '/service-worker.js';

const COPY_CACHE = 'push-copy-v1';
const COPY_URL = '/push-copy.json';

/**
 * Whether this browser can do any of it at all.
 *
 * Three separate capabilities, and all three are genuinely missing somewhere
 * that matters: an iPhone in a Safari tab has no PushManager (only an
 * installed web app does), and a private window can be missing serviceWorker
 * entirely.
 */
export const isPushSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * On an iPhone, push exists only for an app that has been added to the home
 * screen -- not in a Safari tab, and there is no way around it.
 *
 * Worth knowing separately from isPushSupported because the two need different
 * sentences: "tu navegador no puede" is wrong and unhelpful when the honest
 * answer is "añade la app a tu pantalla de inicio y podrás".
 */
export const isIosWithoutInstall = (): boolean => {
  if (typeof window === 'undefined') return false;
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isInstalled =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS's own pre-standard flag, still the only reliable one there.
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return isIos && !isInstalled;
};

export const getPushPermission = (): NotificationPermission =>
  isPushSupported() ? Notification.permission : 'denied';

/** The base64url VAPID key the browser needs, from the API -- never compiled in, because the pair is per environment. */
const fetchPublicKey = async (): Promise<string> => {
  const response = await apiService<{ publicKey: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_PUSH_PUBLIC_KEY,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success && response.data?.publicKey) return response.data.publicKey;
  // 503 here means this environment has no keys, which is a deployment fact
  // rather than something the person did -- the caller turns it into "no
  // disponible" rather than an error.
  throw new Error('PushNotConfigured');
};

/** The browser wants the key as bytes, and hands us base64url. */
const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const raw = window.atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

/** Registers the push worker, or returns the registration that already exists. */
const registration = async (): Promise<ServiceWorkerRegistration> => {
  const existing = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SERVICE_WORKER_URL);
};

/**
 * Hands the service worker the sentences it will need, in the language the
 * person is reading the app in.
 *
 * **This is what lets the push payload carry no prose.** The dictionary is
 * written into Cache Storage -- not localStorage, which a service worker cannot
 * read -- straight out of the running bundle's own translations, so the copy
 * has exactly one home and a notification type added tomorrow gets its push
 * title for free.
 *
 * Called on every enable and whenever the language changes. Failing is not
 * worth reporting: the worker falls back to a generic title, which is a worse
 * notification rather than a broken one.
 */
export const syncPushCopy = async (titles: Record<string, string>): Promise<void> => {
  try {
    const cache = await caches.open(COPY_CACHE);
    await cache.put(COPY_URL, new Response(JSON.stringify({ titles }), {
      headers: { 'Content-Type': 'application/json' },
    }));
  } catch {
    // No Cache Storage (private mode, blocked site data). The worker copes.
  }
};

/**
 * Ask, subscribe, and tell the server where to send.
 *
 * Must be called from a real user gesture: every browser refuses a permission
 * prompt that nobody asked for, and Safari does not even queue it.
 *
 * Returns false when the person said no. That is an answer, not a failure, and
 * the caller shows nothing angry about it.
 */
export const enablePush = async (titles: Record<string, string>): Promise<boolean> => {
  if (!isPushSupported()) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const publicKey = await fetchPublicKey();
  const worker = await registration();

  // An existing subscription is reused rather than replaced: it is the same
  // address, the server stores it idempotently, and unsubscribing first would
  // throw away a permission the person has already granted if anything below
  // fails.
  const subscription =
    (await worker.pushManager.getSubscription()) ??
    (await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const json = subscription.toJSON();
  const response = await apiService({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_PUSH_DEVICES,
    method: HTTP_METHODS.POST,
    body: {
      endpoint: subscription.endpoint,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to register this device');

  await syncPushCopy(titles);
  return true;
};

/**
 * Stop being buzzed on this device.
 *
 * The server is told first. If the order were the other way round and the call
 * failed, the browser would have thrown away an address the server still
 * believes in -- and pushes would keep being sent into nothing forever, with
 * nobody able to stop them from this screen.
 *
 * The browser's own permission is NOT revoked, because no site can do that:
 * turning this back on later is one tap rather than a trip through browser
 * settings.
 */
export const disablePush = async (): Promise<void> => {
  if (!isPushSupported()) return;

  const worker = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
  const subscription = await worker?.pushManager.getSubscription();
  if (!subscription) return;

  await apiService({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_PUSH_DEVICES,
    method: HTTP_METHODS.DELETE,
    body: { endpoint: subscription.endpoint },
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  await subscription.unsubscribe().catch(() => undefined);
};

/** Whether this device currently holds a subscription -- the honest answer to "am I getting notifications here". */
export const hasPushSubscription = async (): Promise<boolean> => {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  try {
    const worker = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
    return !!(await worker?.pushManager.getSubscription());
  } catch {
    return false;
  }
};
